import type {CodexNotification} from '@reados/contracts';

/** The Web conversation owns this history even when a legacy annotation event
 * has no native threadId. Keep that ownership outside the original event. */
export interface ResponseAnnotationHistory {
  ownerThreadId:string;
  nativeThreadId?:string;
  events:CodexNotification[];
}

const nonempty=(value:unknown):value is string=>typeof value==='string'&&value.length>0;
function annotationTurn(event:CodexNotification,nativeThreadId?:string):string|undefined {
  if(event.method!=='reados/responseAnnotations'||!event.params||!nonempty(event.params.turnId)
    ||!Array.isArray(event.params.responseAnnotations)||!event.params.responseAnnotations.length)return;
  const explicitThreadId=event.params.threadId;
  if(explicitThreadId!==undefined&&!nonempty(explicitThreadId))return;
  return JSON.stringify([explicitThreadId??nativeThreadId??'',event.params.turnId]);
}

/** History fills gaps; a GET begun before an SSE update must not overwrite it.
 * The server owns immutable accepted-turn receipts, so replay is idempotent. */
export function mergeResponseAnnotationHistory(current:ResponseAnnotationHistory|null,incoming:readonly CodexNotification[],ownerThreadId:string,nativeThreadId?:string,source:'history'|'live'='live'):ResponseAnnotationHistory {
  const sameOwner=current?.ownerThreadId===ownerThreadId;
  // Forks retain inherited messages/events with their original native identity.
  // The authenticated Web owner scopes access; the renderer still joins each
  // explicit native thread/turn only to its actual stored user message.
  const previous=sameOwner?current!.events:[];
  const native=nativeThreadId||(sameOwner?current?.nativeThreadId:undefined);
  const merged=new Map<string,CodexNotification>();
  const batches=source==='history'?[incoming,previous]:[previous,incoming];
  for(const events of batches)for(const event of events){
    const key=annotationTurn(event,native);
    if(key)merged.set(key,event);
  }
  return {ownerThreadId,...(native?{nativeThreadId:native}:{}),events:[...merged.values()]};
}

export function responseAnnotationEventsForThread(history:ResponseAnnotationHistory|null,ownerThreadId:string|null):CodexNotification[] {
  if(!history||history.ownerThreadId!==ownerThreadId)return [];
  return history.events;
}
